import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';
import { createVideoFromSequence } from '@/utils/autoVideoCreation';
import { incrementalSyncService } from '@/lib/search/typesense/incrementalSyncService';


type Sequence = Database['public']['Tables']['sequences']['Row'];
type Artist = Database['public']['Tables']['artists']['Row'];
type Genre = Database['public']['Tables']['genres']['Row'];

export interface SequenceWithMetadata extends Sequence {
  artist?: Artist;
  genre?: Genre;
}

export interface UserWithSequences {
  user_id: string;
  display_name: string | null;
  username: string | null;
  full_name: string | null;
  sequence_count: number;
}

// Valid sequence table columns based on current schema
const VALID_SEQUENCE_COLUMNS = [
  'id', 'name', 'title', 'description', 'duration', 'created_at', 'updated_at',
  'display_id', 'user_id', 'artist_id', 'genre_id', 'file_url', 'image_url', 
  'video_url', 'album_cover_url', 'spotify_track_id', 'spotify_explicit', 
  'spotify_preview_url', 'spotify_duration_ms', 'spotify_popularity', 'album',
  'price', 'download_count', 'featured', 'is_free', 'software_type',
  'claim_requested_at', 'claim_status', 'claim_verified_at', 'original_creator_info',
  'holiday_type', 'file_type',
  // New multi-genre fields
  'primary_genre', 'secondary_genre', 'genres', 'genres_overridden'
] as const;

// Helper function to safely filter sequence data to only include valid database columns
const filterSequenceData = (data: Partial<SequenceWithMetadata>): Partial<Sequence> => {
  const filtered: Partial<Sequence> = {};
  const invalidFields: string[] = [];
  
  for (const key in data) {
    if (VALID_SEQUENCE_COLUMNS.includes(key as any)) {
      const value = data[key as keyof typeof data];
      if (value !== undefined) {
        (filtered as any)[key] = value;
      }
    } else {
      // Log any invalid fields being filtered out
      invalidFields.push(key);
    }
  }
  
  // Explicitly remove any artist/genre fields that might have leaked through
  if ('artist' in (filtered as any)) {
    delete (filtered as any).artist;
    invalidFields.push('artist (explicitly removed)');
  }
  if ('genre' in (filtered as any)) {
    delete (filtered as any).genre;
    invalidFields.push('genre (explicitly removed)');
  }
  
  // Remove the ID field from updates to avoid conflicts
  if ('id' in (filtered as any)) {
    delete (filtered as any).id;
    invalidFields.push('id (removed from update data)');
  }
  
  // Log debugging information
  if (invalidFields.length > 0) {
    console.log('Filtered out invalid sequence fields:', invalidFields);
  }
  console.log('Valid sequence data being sent to Supabase:', filtered);
  
  // Validate that critical foreign key fields are UUIDs if present
  if (filtered.artist_id && typeof filtered.artist_id !== 'string') {
    console.error('Invalid artist_id type:', typeof filtered.artist_id, filtered.artist_id);
    delete filtered.artist_id;
  }
  if (filtered.genre_id && typeof filtered.genre_id !== 'string') {
    console.error('Invalid genre_id type:', typeof filtered.genre_id, filtered.genre_id);
    delete filtered.genre_id;
  }
  
  return filtered;
};

export function useAdminSequences() {
  const [sequences, setSequences] = useState<SequenceWithMetadata[]>([]);
  const [artists, setArtists] = useState<Artist[]>([]);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch sequences with their metadata
  const fetchSequences = async () => {
    try {
      setLoading(true);
      
      // First, get the total count
      const { count, error: countError } = await supabase
        .from('sequences')
        .select('*', { count: 'exact', head: true });
      
      if (countError) throw countError;
      
      const totalCount = count || 0;
      const pageSize = 1000; // Max allowed by Supabase
      const allSequences: any[] = [];
      
      // Fetch all sequences in batches without joins to avoid schema cache issues
      for (let offset = 0; offset < totalCount; offset += pageSize) {
        const { data, error } = await supabase
          .from('sequences')
          .select('*')
          .order('created_at', { ascending: false })
          .range(offset, offset + pageSize - 1);
        
        if (error) throw error;
        if (data) allSequences.push(...data);
      }
      
      // Fetch artists and genres separately and map them
      const { data: artistsData } = await supabase
        .from('artists')
        .select('*');
      
      const { data: genresData } = await supabase
        .from('genres')
        .select('*');
      
      // Map artist and genre data to sequences
      const sequencesWithMetadata = allSequences.map(sequence => ({
        ...sequence,
        artist: artistsData?.find(a => a.id === sequence.artist_id),
        genre: genresData?.find(g => g.id === sequence.genre_id)
      }));
      
      setSequences(sequencesWithMetadata);
    } catch (err) {
      console.error('Error fetching sequences:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch sequences');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all artists
  const fetchArtists = async () => {
    try {
      const { data, error } = await supabase
        .from('artists')
        .select('*')
        .order('name');

      if (error) throw error;
      setArtists(data || []);
    } catch (err) {
      console.error('Error fetching artists:', err);
    }
  };

  // Fetch all genres
  const fetchGenres = async () => {
    try {
      const { data, error } = await supabase
        .from('genres')
        .select('*')
        .order('name');

      if (error) throw error;
      setGenres(data || []);
    } catch (err) {
      console.error('Error fetching genres:', err);
    }
  };

  // Normalize artist name for consistent searching
  const normalizeArtistName = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[-\s]+/g, ' ')
      .replace(/[^\w\s]/g, '')
      .trim();
  };

  // Find existing artist by name (case-insensitive)
  const findArtistByName = async (name: string): Promise<Artist | null> => {
    try {
      const normalizedName = normalizeArtistName(name);
      
      // Search in database directly for most accurate results
      const { data, error } = await supabase
        .from('artists')
        .select('*')
        .ilike('name', name); // Case-insensitive search
      
      if (error) throw error;
      
      // Find exact match using normalization
      const exactMatch = data?.find(artist => 
        normalizeArtistName(artist.name) === normalizedName
      );
      
      return exactMatch || null;
    } catch (err) {
      console.error('Error finding artist by name:', err);
      return null;
    }
  };

  // Create new artist with upsert logic
  const createArtist = async (name: string, description?: string, imageUrl?: string) => {
    try {
      // First check if artist already exists
      const existingArtist = await findArtistByName(name);
      
      if (existingArtist) {
        console.log(`Artist "${name}" already exists, returning existing artist:`, existingArtist);
        return existingArtist;
      }

      // Create new artist
      const { data, error } = await supabase
        .from('artists')
        .insert([{
          name,
          description,
          image_url: imageUrl
        }])
        .select()
        .single();

      if (error) {
        // Handle duplicate key error
        if (error.code === '23505' || error.message?.includes('duplicate key')) {
          console.log(`Duplicate artist detected for "${name}", fetching existing...`);
          const existingArtist = await findArtistByName(name);
          if (existingArtist) {
            return existingArtist;
          }
        }
        throw error;
      }
      
      console.log(`Created new artist: "${name}"`, data);
      
      // Refresh artists list
      await fetchArtists();
      
      // TODO: Sync to Typesense
      await syncArtistToTypesense(data);
      
      return data;
    } catch (err) {
      console.error('Error creating artist:', err);
      throw err;
    }
  };

  // Sync artist to Typesense by updating all sequences that reference this artist
  const syncArtistToTypesense = async (artist: Artist) => {
    try {
      console.log(`Syncing artist "${artist.name}" to Typesense...`);
      
      // Import Typesense client
      const { Client } = await import('typesense');
      
      const typesenseClient = new Client({
        nodes: [
          {
            host: 'tqpi62sg9v75l4fxp-1.a1.typesense.net',
            port: 443,
            protocol: 'https',
          },
        ],
        apiKey: 'qSAmqZRH5VVmbXQ2QeS69OyVvrqThBwE', // Admin API key
        connectionTimeoutSeconds: 10,
      });

      // Find all sequences that reference this artist
      const { data: sequences, error } = await supabase
        .from('sequences')
        .select('id, title, name, created_at, updated_at')
        .eq('artist_id', artist.id);

      if (error) {
        console.error('Error fetching sequences for artist sync:', error);
        return;
      }

      if (!sequences || sequences.length === 0) {
        console.log(`No sequences found for artist "${artist.name}"`);
        return;
      }

      console.log(`Found ${sequences.length} sequences to update for artist "${artist.name}"`);

      // Transform and update each sequence in Typesense
      const updatePromises = sequences.map(async (sequence) => {
        try {
          const updateData = {
            id: sequence.id.toString(),
            artist_name: artist.name
          };

          await typesenseClient
            .collections('sequences_v2')
            .documents(sequence.id.toString())
            .update(updateData);

          console.log(`Updated sequence "${sequence.title || sequence.name}" with artist "${artist.name}"`);
        } catch (updateError) {
          console.error(`Failed to update sequence ${sequence.id} in Typesense:`, updateError);
          // Continue with other sequences even if one fails
        }
      });

      // Execute all updates with a small delay between batches to avoid rate limiting
      const batchSize = 10;
      for (let i = 0; i < updatePromises.length; i += batchSize) {
        const batch = updatePromises.slice(i, i + batchSize);
        await Promise.all(batch);
        
        // Small delay between batches
        if (i + batchSize < updatePromises.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      console.log(`Successfully synced artist "${artist.name}" to Typesense`);
    } catch (err) {
      console.error('Failed to sync artist to Typesense:', err);
      // Don't throw - this shouldn't block artist creation
    }
  };

  // Create new genre
  const createGenre = async (name: string, description?: string) => {
    try {
      // First check if a genre with this name already exists (case-insensitive)
      const { data: existingGenre, error: searchError } = await supabase
        .from('genres')
        .select('*')
        .ilike('name', name)
        .single();

      if (searchError && searchError.code !== 'PGRST116') {
        // PGRST116 means no rows found, which is what we want
        throw searchError;
      }

      // If genre already exists, return it
      if (existingGenre) {
        console.log(`Genre "${name}" already exists, returning existing genre`);
        return existingGenre;
      }

      // Create new genre if it doesn't exist
      const { data, error } = await supabase
        .from('genres')
        .insert([{
          name,
          description
        }])
        .select()
        .single();

      if (error) throw error;
      
      // Refresh genres list
      await fetchGenres();
      return data;
    } catch (err) {
      console.error('Error creating genre:', err);
      throw err;
    }
  };

  // Create new sequence
  const createSequence = async (sequence: Partial<SequenceWithMetadata>) => {
    try {
      // Use the safe filtering function to ensure only valid database columns are included
      const filteredSequence = filterSequenceData(sequence);

      console.log('Filtered sequence for Supabase create:', filteredSequence);

      // Use regular client for admin operations
      const { data: newSequence, error } = await supabase
        .from('sequences')
        .insert([filteredSequence])
        .select('*')
        .single();

      if (error) throw error;

      // Auto-create video if video_url is provided
      if (sequence.video_url && newSequence) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          console.log('Creating video from sequence URL:', sequence.video_url);
          const videoResult = await createVideoFromSequence(
            newSequence.id,
            sequence.video_url,
            user.id,
            {
              title: sequence.title || sequence.name,
              name: sequence.name,
              artist: sequence.artist_id ? 'Unknown Artist' : undefined, // Could be enhanced with artist lookup
              album: sequence.album,
              spotify_track_id: sequence.spotify_track_id,
              holiday_type: sequence.holiday_type
            }
          );

          if (videoResult.success) {
            console.log('Video created successfully:', videoResult.videoId);
          } else {
            console.warn('Failed to create video:', videoResult.error);
          }
        }
      }
      
      // Index the new sequence in Typesense
      try {
        await incrementalSyncService.syncSequence(newSequence.id, 'insert');
        console.log('✅ New sequence indexed in Typesense successfully');
      } catch (syncError) {
        console.warn('⚠️ Failed to index new sequence in Typesense:', syncError);
        // Don't fail the sequence creation if indexing fails
        // The database trigger will retry automatically
      }
      
      // Refresh sequences list to get updated data
      await fetchSequences();
      
      return newSequence;
    } catch (err) {
      console.error('Error creating sequence:', err);
      throw err;
    }
  };

  // Update sequence
  const updateSequence = async (id: string, updates: Partial<SequenceWithMetadata>) => {
    try {
      // Validate that we have an ID
      if (!id) {
        throw new Error('Sequence ID is required for update');
      }

      // Debug: Check current authentication status
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('Auth error:', authError);
        throw new Error('Authentication failed');
      }
      if (!user) {
        throw new Error('User not authenticated');
      }
      console.log('Authenticated user:', user.email, 'ID:', user.id);

      // Use the safe filtering function to ensure only valid database columns are included
      const filteredUpdates = filterSequenceData(updates);
      
      // Explicitly set updated_at to ensure timestamp is updated
      filteredUpdates.updated_at = new Date().toISOString();

      console.log('Updating sequence:', id, 'with filtered data:', filteredUpdates);

      // Check if we have any valid updates after filtering
      if (Object.keys(filteredUpdates).length <= 1) { // Only updated_at
        throw new Error('No valid fields to update');
      }

      // Use regular Supabase client - RLS policies will handle admin permissions
      console.log('Using regular Supabase client for update operation');

      // Use regular Supabase client for the update operation
      const { data, error } = await supabase
        .from('sequences')
        .update(filteredUpdates)
        .eq('id', id)
        .select(); // Make sure to select to get the updated data

      if (error) {
        console.error('Supabase update error:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        
        // Provide more specific error messages
        if (error.message?.includes('WHERE clause')) {
          throw new Error(`Database update failed: Missing WHERE clause. This may be a RLS policy issue.`);
        } else if (error.code === '42501') {
          throw new Error(`Permission denied: User does not have permission to update this sequence.`);
        } else {
          throw new Error(`Database update failed: ${error.message} (Code: ${error.code})`);
        }
      }

      // Check if any rows were actually updated
      if (!data || data.length === 0) {
        console.warn('No rows updated - this could indicate RLS policy blocking the update');
        throw new Error(`No sequence found with ID ${id} or update was blocked by permissions.`);
      }

      console.log('Successfully updated sequence:', data[0].id);

      // Auto-create video if video_url is provided or updated
      if (updates.video_url && data[0]) {
        console.log('Creating video from updated sequence URL:', updates.video_url);
        const videoResult = await createVideoFromSequence(
          data[0].id,
          updates.video_url,
          user.id,
          {
            title: updates.title || data[0].title || updates.name || data[0].name,
            name: updates.name || data[0].name,
            artist: updates.artist_id || data[0].artist_id ? 'Unknown Artist' : undefined, // Could be enhanced with artist lookup
            album: updates.album || data[0].album,
            spotify_track_id: updates.spotify_track_id || data[0].spotify_track_id,
            holiday_type: updates.holiday_type || data[0].holiday_type
          }
        );

        if (videoResult.success) {
          console.log('Video created successfully:', videoResult.videoId);
        } else {
          console.warn('Failed to create video:', videoResult.error);
        }
      }
      
      // Index the updated sequence in Typesense
      try {
        await incrementalSyncService.syncSequence(id, 'update');
        console.log('✅ Updated sequence indexed in Typesense successfully');
      } catch (syncError) {
        console.warn('⚠️ Failed to index updated sequence in Typesense:', syncError);
        // Don't fail the sequence update if indexing fails
        // The database trigger will retry automatically
      }
      
      // Refresh sequences list to get updated data
      await fetchSequences();
      
      return data[0];
    } catch (err) {
      console.error('Error updating sequence:', err);
      throw err;
    }
  };

  // Upload file to sequences bucket using unified signed upload flow
  const uploadSequenceFile = async (file: File, sequenceId: string) => {
    try {
      // Get current user for upload
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Authentication required for file upload');
      }

      console.log('🔐 Auth check passed:', {
        userId: user.id,
        email: user.email
      });

      // 1) Ask server for signed upload URL for this file (include auth)
      const { data: sess } = await supabase.auth.getSession();
      const accessToken = sess.session?.access_token;
      if (!accessToken) throw new Error('Missing access token');

      const signedRes = await fetch('/api/storage/signed-upload', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ userId: user.id, fileName: file.name })
      });

      if (!signedRes.ok) {
        const errData = await signedRes.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to create signed upload URL');
      }

      const { path, token } = await signedRes.json();

      // 2) Upload directly to Supabase Storage using the signed URL token
      const { error: uploadError } = await supabase.storage
        .from('sequences')
        .uploadToSignedUrl(path, token, file);

      if (uploadError) {
        throw new Error(uploadError.message || 'Upload failed');
      }

      // 3) Notify server to persist metadata on sequence
      await fetch('/api/storage/complete', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          userId: user.id,
          sequenceId,
          path,
          originalFileName: file.name,
          size: file.size,
          contentType: file.type || 'application/octet-stream'
        })
      });

      console.log('✅ Upload successful via signed URL:', { path });

      return { path };
    } catch (err) {
      console.error('Error uploading file:', err);
      throw err;
    }
  };

  // Find sequences missing metadata
  // Find potential duplicate sequences
  const findDuplicateSequences = async () => {
    try {
      const { data, error } = await supabase
        .from('sequences')
        .select('id, title, name, user_id, created_at, file_url')
        .order('title');

      if (error) throw error;

      const duplicateGroups: { [key: string]: typeof data } = {};
      
      data?.forEach(sequence => {
        const key = (sequence.title || sequence.name || '').toLowerCase().trim();
        if (!duplicateGroups[key]) {
          duplicateGroups[key] = [];
        }
        duplicateGroups[key].push(sequence);
      });

      // Filter to only groups with multiple entries
      const duplicates = Object.entries(duplicateGroups)
        .filter(([_, sequences]) => sequences.length > 1)
        .map(([title, sequences]) => ({ title, sequences }));

      return duplicates;
    } catch (err) {
      console.error('Error finding duplicate sequences:', err);
      return [];
    }
  };

  const findSequencesMissingMetadata = async () => {
    try {
      // First, get the total count
      const { count, error: countError } = await supabase
        .from('sequences')
        .select('*', { count: 'exact', head: true })
        .or('spotify_track_id.is.null,artist_id.is.null,genre_id.is.null,spotify_popularity.is.null');
      
      if (countError) throw countError;
      
      const totalCount = count || 0;
      const pageSize = 1000; // Max allowed by Supabase
      const allSequences: Sequence[] = [];
      
      // Fetch all sequences with missing metadata in batches
      for (let offset = 0; offset < totalCount; offset += pageSize) {
        const { data, error } = await supabase
          .from('sequences')
          .select('*')
          .or('spotify_track_id.is.null,artist_id.is.null,genre_id.is.null,spotify_popularity.is.null')
          .order('created_at', { ascending: false })
          .range(offset, offset + pageSize - 1);
        
        if (error) throw error;
        if (data) allSequences.push(...data);
      }
      
      return allSequences;
    } catch (err) {
      console.error('Error finding sequences missing metadata:', err);
      throw err;
    }
  };

  // Fetch users who own sequences
  const fetchUsersWithSequences = async (): Promise<UserWithSequences[]> => {
    try {
      // First, get all sequences with user_id
      const { data: sequences, error: sequencesError } = await supabase
        .from('sequences')
        .select('user_id')
        .not('user_id', 'is', null);

      if (sequencesError) throw sequencesError;

      // Get unique user IDs
      const userIds = [...new Set(sequences?.map(seq => seq.user_id) || [])];
      
      if (userIds.length === 0) {
        return [];
      }

      // Fetch user profiles separately
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('user_id, display_name, username, full_name')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      // Create a map of user profiles
      const profileMap = new Map();
      profiles?.forEach(profile => {
        profileMap.set(profile.user_id, profile);
      });

      // Group by user and count sequences
      const userMap = new Map<string, UserWithSequences>();
      
      sequences?.forEach((sequence: any) => {
        const userId = sequence.user_id;
        const profile = profileMap.get(userId);
        
        if (userMap.has(userId)) {
          const user = userMap.get(userId)!;
          user.sequence_count++;
        } else {
          userMap.set(userId, {
            user_id: userId,
            display_name: profile?.display_name || null,
            username: profile?.username || null,
            full_name: profile?.full_name || null,
            sequence_count: 1
          });
        }
      });

      // Sort by display name, then username
      const users = Array.from(userMap.values()).sort((a, b) => {
        const nameA = a.display_name || a.username || a.full_name || '';
        const nameB = b.display_name || b.username || b.full_name || '';
        return nameA.localeCompare(nameB);
      });

      return users;
    } catch (err) {
      console.error('Error fetching users with sequences:', err);
      return [];
    }
  };

  // Delete a sequence (admin only)
  const deleteSequence = async (sequenceId: string): Promise<void> => {
    try {
      const response = await fetch(`/api/admin/sequences/${sequenceId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });

      if (!response.ok) {
        // The endpoint might return HTML (e.g., 404 page). Try to parse JSON; otherwise fall back to text.
        let message = 'Failed to delete sequence';
        try {
          const errorJson = await response.json();
          message = errorJson?.error || message;
        } catch {
          try {
            const text = await response.text();
            if (text) message = text;
          } catch {}
        }
        throw new Error(message);
      }

      // Result should be JSON; fall back to empty object if not
      let result: any;
      try {
        result = await response.json();
      } catch {
        result = { success: true };
      }
      console.log('Sequence deleted:', result);

      // Remove the sequence from Typesense
      try {
        await incrementalSyncService.syncSequence(sequenceId, 'delete');
        console.log('✅ Sequence removed from Typesense successfully');
      } catch (syncError) {
        console.warn('⚠️ Failed to remove sequence from Typesense:', syncError);
        // Don't fail the sequence deletion if indexing fails
      }

      // Refresh the sequences list
      await fetchSequences();
      
      return result;
    } catch (err) {
      console.error('Error deleting sequence:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchSequences();
    fetchArtists();
    fetchGenres();
  }, []);

  return {
    sequences,
    artists,
    genres,
    loading,
    error,
    fetchSequences,
    fetchArtists,
    fetchGenres,
    fetchUsersWithSequences,
    findArtistByName,
    createArtist,
    createGenre,
    createSequence,
    updateSequence,
    uploadSequenceFile,
    findSequencesMissingMetadata,
    findDuplicateSequences,
    deleteSequence
  };
}